import { atsStore, lookupStore } from "stores";
import { Constants, Enum } from "helpers";
import { convertBase64toFileObject } from "helpers/FileProcessing";
import { observable, action } from "mobx";

let inputAtsFields = [
  'candidate_address1',
  'address1',
  'candidate_address2',
  'address2',
  'candidate_city',
  'city',
  'candidate_suburb',
  'suburb',
  'candidate_current_employer',
  'current_employer',
  'candidate_first_name',
  'first_name',
  'candidate_last_name',
  'last_name',
  'candidate_current_job_title',
  'current_job_title',
  'candidate_state',
  'state',
  'candidate_desired_job_title',
  'candidate_zip',
  'zip',
  'candidate_emergency_contact',
];

let dropDownAtsFields = [
  'candidate_country',
  'country',
  'candidate_current_work_type',
  'current_work_type',
  'candidate_desired_pay_frequency',
  'desired_pay_frequency',
  'candidate_current_pay_frequency',
  'current_pay_frequency',
  'candidate_desired_work_type',
  'candidate_skill_category',
  'candidate_skill_subcategory',
  'candidate_skill',
  'candidate_status',
  'candidate_salutation',
  'candidate_notice_period',
];

// Fields offered on the Radio buttons block's "Integrated fields" option.
// Work type/pay frequency are already candidate Dropdown fields (EXPAND to Radio);
// status/contact method are Radio-only additions (candidate_salutation has no
// Radio variant in scope).
let radioAtsFields = [
  'candidate_current_work_type',
  'current_work_type',
  'candidate_desired_work_type',
  'candidate_desired_pay_frequency',
  'desired_pay_frequency',
  'candidate_current_pay_frequency',
  'current_pay_frequency',
  'candidate_status',
];

let checkboxesAtsFields = [
  'candidate_skill_category',
  'candidate_skill_subcategory',
  'candidate_skill',
];

export const phoneAtsFields = [
  'candidate_mobile',
  'mobile',
  'candidate_phone',
  'phone',
  'candidate_emergency_phone'
];

export const dateAtsFields = [
  'candidate_availability_date',
  'availability.date',
  'candidate_date_of_birth'
];

export const yesNoAtsFields = [
  'candidate_available_immediately'
];

export const fileUploadAtsFields = [
  // 'candidate_photo',
  'candidate_resume'
];

export const numberAtsFields = [
  'candidate_desired_low_salary',
  'desired_low_salary',
  'candidate_desired_high_salary',
  'desired_high_salary',
  'candidate_current_salary',
  'current_salary'
];

export const emailAtsFields = [
  'candidate_email',
  'email'
];

export const linkedinAtsFields = [
  'candidate_linkedin',
  'linkedin'
];

export const scaleAtsFields = [
  'candidate_nps'
];

/**
 * Extracts the actual slot name from a potentially alias-prefixed field name.
 *
 * When a Tripetto section has an alias, Export.exportables() prefixes the
 * slot name with the section alias + " / " separator.
 * e.g. "Alias1 / ats-candidate_state" → "ats-candidate_state"
 *
 * Confirmed by live submission data.
 */
const getSlotName = (name) => {
  if (!name) return name;
  // Tripetto uses " / " as the default separator between section alias and slot name
  const parts = name.split(' / ');
  return parts[parts.length - 1];
};

export const isNameHasAts = (name) => {
  // Strip any section alias prefix before checking for "ats-" prefix
  // e.g. "Alias1 / ats-candidate_state" → "ats-candidate_state"
  const slotName = getSlotName(name);
  // check if have ats on first data before (-),, example "ats-first_name-textinline"
  if (slotName?.split('-', 3)[0] === "ats") {
    return true;
  }

  return false;
};

export const getAtsTokenName = (name) => {
  // Strip any section alias prefix before extracting the ATS token
  const slotName = getSlotName(name);
  // get the value after first dash (-),, example "ats-first_name-textinline" -> get the "first_name"
  return slotName?.split('-', 3)[1];
};

export const splitCheckboxesReference = (data) => {
  const checkboxesReference = data.split("\n");
  const referenceData = checkboxesReference[1].split("-");
  const field = referenceData[1] || "";
  const fieldId = referenceData[referenceData.length - 1];

  // Check if checkboxes are candidate_skill_category, candidate_skill_subcategory, or candidate_skill
  if (isSkillField(field)) {
    const values = [];
    checkboxesReference.forEach(ref => {
      if (ref.trim() !== "") {
        const referenceData = ref.split("-");
        const value = referenceData[referenceData.length - 1];

        // Only add valid numeric fieldIds and avoid duplicates
        if (value && !isNaN(value) && !values.includes(value)) {
          values.push(value);
        }
      }
    });
    return { atsField: field, atsFieldId: undefined, atsValue: values };
  }

  const customFieldData = tripettoStore.listCustomFields().filter(item => item.fieldId === parseInt(fieldId));

  const atsData = {
    atsField: customFieldData.length > 0 ? customFieldData[0].tokenVariable.replace("-" + fieldId, "") : "",
    atsFieldId: fieldId,
    atsValue: undefined
  }
  return atsData
};

const isSkillField = (field) => {
  return ["candidate_skill_category", "candidate_skill_subcategory", "candidate_skill"].includes(field);
};

// atsFieldCandidate/atsFieldClient are filtered by ContactType (who can receive it),
// not by AtsFieldCategoryType (what kind of record it describes) — so both arrays
// contain Placement/Job/Company/Interview tags too. A survey token only ever resolves
// against a candidate or contact record, so merge tags are restricted to those two
// categories (AtsFieldCategoryType 0 = Candidate, 2 = Contact) specifically.
const MERGE_TAG_CATEGORY_TYPE = { CANDIDATE: 0, CONTACT: 2 };

const dedupeMergeTagFields = (fields, categoryTypeId) => {
  const seen = new Set();
  return fields
    .filter(field => field.categoryType === categoryTypeId)
    .filter(field => {
      if (!field.isShow || !field.tokenVariable || seen.has(field.tokenVariable)) return false;
      seen.add(field.tokenVariable);
      return true;
    })
    .map(field => ({
      name: field.tokenVariable,
      description: atsStore.stripGroupPrefix(field.displayName, categoryTypeId)
    }));
};

// Merge tags available for embedding in survey question prompt/help text.
// Surveys only — forms never use these (public forms have no recipient record).
export const listMergeTagVariables = () => [
  ...dedupeMergeTagFields(atsStore.atsFieldCandidate, MERGE_TAG_CATEGORY_TYPE.CANDIDATE),
  ...dedupeMergeTagFields(atsStore.atsFieldClient, MERGE_TAG_CATEGORY_TYPE.CONTACT)
];

// Same tags as listMergeTagVariables, grouped under "Candidate" / "Contact" headings
// for the survey builder's Merge tags dropdown.
export const listMergeTagVariableGroups = () => [
  { group: "Candidate", tags: dedupeMergeTagFields(atsStore.atsFieldCandidate, MERGE_TAG_CATEGORY_TYPE.CANDIDATE) },
  { group: "Contact", tags: dedupeMergeTagFields(atsStore.atsFieldClient, MERGE_TAG_CATEGORY_TYPE.CONTACT) }
].filter(section => section.tags.length > 0);

// Matches {{merge_tag}} tokens embedded in survey question prompt or help text.
const MERGE_TAG_PATTERN = /{{\s*([A-Za-z0-9_][A-Za-z0-9_\-. ]*?)\s*}}/g;

// Replaces merge tags in a survey definition's text. The builder preview uses this with
// placeholder labels; real recipient values are substituted at delivery (ROI-Chat).
// Unknown tags are left as typed so authors can spot mistakes.
export const substituteMergeTags = (definition, mergeTags) => {
  if (!definition) return definition;

  const values = mergeTags || {};
  const json = JSON.stringify(definition).replace(MERGE_TAG_PATTERN, (match, token) => {
    if (!Object.prototype.hasOwnProperty.call(values, token)) return match;
    // escape the replacement for a JSON string context
    return JSON.stringify(String(values[token] ?? "")).slice(1, -1);
  });

  return JSON.parse(json);
};

// Placeholder values shown in the builder preview, e.g. {{candidate_mobile}} → [Candidate Mobile]
export const previewMergeTagValues = () => listMergeTagVariables().reduce((values, tag) => {
  values[tag.name] = `[${tag.description}]`;
  return values;
}, {});

const RADIOBUTTONS_BLOCK_TYPE = "@tripetto/block-radiobuttons";

// All three ROI-Tripetto forks (ROI-UI, ROI-Chat, ROI-Forms) now agree the radiobuttons block's
// static answer slot is referenced as "button" (the stock Tripetto convention). It briefly used
// "option" in this repo (an inherited copy-paste artifact from the dropdown block, not a
// deliberate choice) before being reverted, and ROI-Chat/ROI-Forms independently normalize
// "button" -> "option" during that same window on their own data. Rather than track which
// specific legacy value a given definition might carry, normalize unconditionally to "button" —
// the block only ever has one static slot (the answer), so any static slot found here that isn't
// already "button" is by definition stale and safe to rewrite.
export const normalizeLegacyRadiobuttonSlots = (definition) => {
  const visitSection = (section) => {
    (section?.nodes || []).forEach(node => {
      if (node?.block?.type !== RADIOBUTTONS_BLOCK_TYPE || !Array.isArray(node.slots)) return;

      node.slots.forEach(slot => {
        if (slot?.kind === "static" && slot.reference !== "button") {
          slot.reference = "button";
        }
      });
    });

    // Branches hang off the section, not the node — a radio placed inside a branch is only
    // reachable through section.branches[].sections[].
    (section?.branches || []).forEach(branch => (branch?.sections || []).forEach(visitSection));
  };

  (definition?.sections || []).forEach(visitSection);

  return definition;
};

export const tripettoStore = observable({
  selectedCategoryIdsMap: {},
  selectedSubCategoryIdsMap: {},
  fieldTypeMap: {},
  // Only the survey builder enables merge tags (forms are public — no recipient record)
  mergeTagsEnabled: false,

  setMergeTagsEnabled: action(function(enabled) {
    this.mergeTagsEnabled = enabled;
  }),

  listMergeTagFields: () => {
    if (!tripettoStore.mergeTagsEnabled) return [];
    return listMergeTagVariableGroups().flatMap(section => [
      { group: section.group },
      ...section.tags.map(tag => ({ label: tag.description, value: tag.name }))
    ]);
  },

  setCategoryId: action(function(nodeId, value) {
    if (this.selectedCategoryIdsMap[nodeId] !== value) {
      this.selectedCategoryIdsMap[nodeId] = value;
    }
  }),

  setSubCategoryId: action(function(nodeId, value) {
    if (this.selectedSubCategoryIdsMap[nodeId] !== value) {
      this.selectedSubCategoryIdsMap[nodeId] = value;
    }
  }),

  removeCategoryId: action(function(nodeId) {
    delete this.selectedCategoryIdsMap[nodeId];
  }),

  removeSubCategoryId: action(function(nodeId) {
    delete this.selectedSubCategoryIdsMap[nodeId];
  }),
  registerFieldType: action(function(nodeId, fieldType) {
    if (this.fieldTypeMap[nodeId] !== fieldType) {
      this.fieldTypeMap[nodeId] = fieldType;
    }
  }),

  listCheckboxesFields: () => {
    return convertDataAtsToTripetto(
      atsStore.integratedAtsFields,
      convertCustomFieldsToTripetto(atsStore.customFields, checkboxesAtsFields, Enum.SurveyCustomFieldType.List, true),
      atsStore.customFields
    );
  },

  listDropdownFields: () => {
    return convertDataAtsToTripetto(
      atsStore.integratedAtsFields,
      convertCustomFieldsToTripetto(atsStore.customFields, dropDownAtsFields, Enum.SurveyCustomFieldType.List),
      atsStore.customFields
    );
  },

  listTextFields: () => {
    return convertDataAtsToTripetto(
      atsStore.integratedAtsFields,
      convertCustomFieldsToTripetto(atsStore.customFields, inputAtsFields, Enum.SurveyCustomFieldType.Text),
      atsStore.customFields
    );
  },

  listPhoneFields: () => {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, phoneAtsFields);
  },

  listDateFields: () => {
    return convertDataAtsToTripetto(
      atsStore.integratedAtsFields,
      convertCustomFieldsToTripetto(atsStore.customFields, dateAtsFields, Enum.SurveyCustomFieldType.Date),
      atsStore.customFields
    );
  },

  listFileFields: () => {
    let list = convertDataAtsToTripetto(atsStore.integratedAtsFields, fileUploadAtsFields);
    if (lookupStore.candidateAttachmentCategories.length) list = lookupStore.candidateAttachmentCategories.map(x => ({ label: x.category, value: x.category }));
    return list;
  },

  // JobAdder custom fields have no dedicated "Number" type — numeric-looking
  // custom fields are synced as Text, so they're sourced the same way here.
  listNumberFields: () => {
    return convertDataAtsToTripetto(
      atsStore.integratedAtsFields,
      convertCustomFieldsToTripetto(atsStore.customFields, numberAtsFields, Enum.SurveyCustomFieldType.Text),
      atsStore.customFields
    );
  },

  listEmailFields: () => {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, emailAtsFields);
  },

  listLinkedInFields: () => {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, linkedinAtsFields);
  },

  listScaleFields: () => {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, scaleAtsFields);
  },

  listRadioFields: () => {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, radioAtsFields);
  },

  // candidate_nps is the only integrated rating field in scope — reuses the
  // same allowlist as Scale so NPS can be bound to either block type.
  listRatingFields: () => {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, scaleAtsFields);
  },

  // Status options come from the live JobAdder candidate status lookup.
  // Excludes the synthetic "Any" entry (statusId 0) used only for list filters.
  listStatusFields: () => {
    return (
      lookupStore.candidateStatuses
        ?.filter((item) => item.statusId)
        .map((item) => ({
          name: item.name,
          label: item.name,
          value: item.statusId.toString()
        })) || []
    );
  },

  // Salutation is a company-configurable JobAdder picklist (Settings > Candidates
  // > Salutation), not a fixed value — sourced live the same way status is. The
  // plain name IS the value to submit.
  listSalutationFields: () => {
    return (
      lookupStore.salutations?.map((item) => ({
        name: item.name,
        label: item.name,
        value: item.name
      })) || []
    );
  },

  listYesNoFields: () => {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, yesNoAtsFields);
  },

  listNoticePeriodFields: () => {
    return (
      Enum.OptionNoticePeriod?.map((item) => ({
        name: item.text,
        label: item.text,
        value: item.value
      })) || []
    );
  },

  listCountries: () => {
    return (
      lookupStore.countries?.map((item) => ({
        name: item.name,
        value: item.code
      })) || []
    );
  },

  listWorkTypes: () => {
    return (
      lookupStore.currentWorkTypes?.map((item) => ({
        name: item.name,
        value: item.workTypeId.toString()
      })) || []
    );
  },

  listPayRates: () => {
    return (
      Enum.OptionSalaryRate?.map((item) => ({
        name: item.text,
        value: item.value
      })) || []
    );
  },

  listCustomFields: () => {
    return (atsStore.customFields.length > 0 ? atsStore.customFields : lookupStore.customFields)
  },

  listSkillCategories: () => lookupStore.skillCategories?.map(c => ({
    name: c.name,
    value: c.categoryId.toString(),
  })) || [],

  listSkillSubCategories: (categoryId) => lookupStore.skillSubCategories?.filter(sc => sc.categoryId === categoryId).map(sc => ({
    name: sc.name,
    value: sc.subCategoryId.toString(),
  })) || [],

  listSkills: (categoryId, subCategoryId) => lookupStore.skills?.filter(s => s.categoryId === categoryId && (s.subCategoryId === 0 || s.subCategoryId === subCategoryId)).map(s => ({
    name: s.name,
    value: s.skillId.toString(),
  })) || [],

  listCheckboxSkillSubCategories(categoryIds) {
    return lookupStore.skillSubCategories?.filter(sc => categoryIds.includes(sc.categoryId)).map(sc => ({
      name: sc.name,
      value: sc.subCategoryId.toString(),
    })) || []
  },

  listCheckboxSkills(categoryIds, subCategoryIds) {
    return lookupStore.skills?.filter(s => categoryIds.includes(s.categoryId) && (s.subCategoryId === 0 || subCategoryIds.includes(s.subCategoryId))).map(s => ({
      name: s.name,
      value: s.skillId.toString(),
    })) || []
  }
});

export const convertTripettoData = (data) => {
  let finalData = [];

  data.fields.forEach(element => {
    let tempData = {};
    tempData.value = element.value === 0 ? "0" : element.value ? element.value.toString() : "";
    tempData.type = element.type;

    // example data: ats-first_name-textinline
    // check if have ats on first data before -
    if (isNameHasAts(element.name) && element.type !== Constants.TripettoNodeType.CHECKBOXES) {
      tempData.atsField = getAtsTokenName(element.name);
      // For date types, use the formatted date string (YYYY-MM-DD) instead of raw timestamp
      // because the backend uses DateTime.TryParse which cannot parse timestamp numbers
      tempData.atsValue = element.type === Constants.TripettoNodeType.DATE
        ? (element.string || tempData.value)
        : tempData.value;
      // Use getSlotName to strip any section alias prefix before splitting
      const slotName = getSlotName(element.name);
      const splitedName = slotName.split("-");
      tempData.fieldId = splitedName.length > 2 ? splitedName[splitedName.length - 1] : "";
    }

    // check if have fileupload in it,, (default stored in 'reference' key)
    if (element.reference && element.slot === Constants.TripettoNodeType.FILE) {
      // get only base64 raw string without type 'base64'
      tempData.fileData = element.reference.split('base64,')[1];
      tempData.contentType = convertBase64toFileObject(element.reference, element.value).type;
      tempData.category = element.name;
    }

    // set value by reference only for DROPDOWN OPTION
    if (element.reference && element.slot === "option") {
      if (isNameHasAts(element.name)) {
        tempData.atsValue = element.reference;
      }
      tempData.value = element.value;
    }

    // add question here
    tempData.question = element.node.props().name;

    // set checkboxes
    const isIntegratedCheckboxes = element.type === Constants.TripettoNodeType.CHECKBOXES && element.slot === "selection";

    if (isIntegratedCheckboxes) {

      if (element.value) {
        const isNullReference = element.reference.includes("ats");
        const checkboxesAtsFields = isNullReference ? splitCheckboxesReference(element.reference) : undefined;
        let checkboxesValue = [];

        if (element.value !== "") {
          checkboxesValue = element.value.split(",");
        }

        tempData.atsField = isNullReference ? checkboxesAtsFields.atsField : undefined;
        if (isSkillField(tempData.atsField)) {
          tempData.atsValue = isNullReference ? JSON.stringify(checkboxesAtsFields.atsValue) : undefined;
        } else {
          tempData.atsValue = isNullReference ? JSON.stringify(checkboxesValue) : undefined;
        }
        tempData.fieldId = isNullReference ? checkboxesAtsFields.atsFieldId : undefined;
        tempData.question = isNullReference ? element.name : element.node.props().name;
        tempData.value = JSON.stringify(checkboxesValue);
      }

    } else if (element.type === Constants.TripettoNodeType.CHECKBOXES && !element.isMapped) {
      let groupedCheckboxes = data.fields.filter(field => field.node.id === element.node.id);
      let checkboxesValue = groupedCheckboxes.filter(field => field.value).map(field => field.name);
      groupedCheckboxes.forEach(field => field.isMapped = true);
      tempData.value = JSON.stringify(checkboxesValue);
      finalData.push(tempData);
    }

    if (isIntegratedCheckboxes ||
      element.type === Constants.TripettoNodeType.DATE ||
      element.type === Constants.TripettoNodeType.DROPDOWN ||
      element.type === Constants.TripettoNodeType.EMAIL ||
      element.type === Constants.TripettoNodeType.FILE ||
      element.type === Constants.TripettoNodeType.NUMBER ||
      element.type === Constants.TripettoNodeType.PHONE ||
      element.type === Constants.TripettoNodeType.TEXT ||
      element.type === Constants.TripettoNodeType.SCALE ||
      element.type === Constants.TripettoNodeType.RADIOBUTTONS ||
      element.type === Constants.TripettoNodeType.RATING ||
      element.type === Constants.TripettoNodeType.YES_NO) {
      finalData.push(tempData);
    }
  });

  return finalData;
};

export const convertCustomFieldsToTripetto = (customFields, fields, type, isMultiSelect = false) => {
  let dataFinal = [...fields];
  customFields.forEach(item => {
    if (item.type === type && item.isMultiSelect === isMultiSelect) {
      dataFinal.push(item.tokenVariable);
    }
  });
  return dataFinal;
};

export const convertDataAtsToTripetto = (atsListData, filterArray, customFields = []) => {
  // final data will look like this (for tripetto purpose)
  // [{
  //   label: "label test",
  //   value: "value test"
  // }]
  let finalData = [];

  atsListData.forEach(element => {
    if (filterArray.includes(element.tokenVariable)) {
      let object = {};
      object.label = element.tokenVariable === 'city' ? "Suburb" : element.tokenVariable === 'candidate_city' ? "Candidate Suburb" : element.displayName;
      object.value = `ats-${element.tokenVariable}`;
      if (!finalData.some(data => data.value === object.value)) {
        finalData.push(object);
      }
    }
  });
  customFields.length > 0 && customFields.forEach(element => {
    if (filterArray.includes(element.tokenVariable)) {
      let object = {};
      object.label = element.displayName;
      object.value = `ats-${element.tokenVariable}`;
      if (!finalData.some(data => data.value === object.value)) {
        finalData.push(object);
      }
    }
  });
  return finalData;
};
